import type {CSSProperties} from 'react';
import type {ValuesFor} from '@gl/types/util.js';

const VariantTypes = ['light', 'accent', 'alt'] as const;
const DEFAULT_VARIANT_TYPE: VariantType = 'alt';
/** Index for extracting the regex group from a match. */
const VARIANT_TYPE_GROUP = 1;
import {getValueByHash} from '@gl/utils/arrays.js';

type VariantType = (typeof VariantTypes)[number];

type ColorStyleProps = {
  color?: Color;
  bg?: Color;
  border?: Color;
};

type ColorMapping = {
  variant: VariantColor;
  base: BaseColor;
  type: VariantType;
};

export const BaseColor = {
  Primary: 'primary',
  Secondary: 'secondary',
  Green: 'green',
  Yellow: 'yellow',
  Red: 'red',
  Background: 'background',
  Body: 'body',
  Heading: 'heading',
} as const;

export const VariantColor = {
  PrimaryLight: 'primary-light',
  SecondaryLight: 'secondary-light',
  GreenLight: 'green-light',
  YellowLight: 'yellow-light',
  RedLight: 'red-light',
  BackgroundAccent: 'background-accent',
  BackgroundAlt: 'background-alt',
  BodyLight: 'body-light',
  BodyAccent: 'body-accent',
  HeadingAccent: 'heading-accent',
} as const;

export const Color = {
  ...BaseColor,
  ...VariantColor,
} as const;

export type BaseColor = ValuesFor<typeof BaseColor>;
export type VariantColor = ValuesFor<typeof VariantColor>;
export type Color = ValuesFor<typeof Color>;

export const colorVarMap: Record<Color, string> = {
  primary: 'var(--fly-color-primary)',
  'primary-light': 'var(--fly-color-primary--light)',
  secondary: 'var(--fly-color-secondary)',
  'secondary-light': 'var(--fly-color-secondary--light)',
  green: 'var(--fly-color-success)',
  'green-light': 'var(--fly-color-success--light)',
  yellow: 'var(--fly-color-warning)',
  'yellow-light': 'var(--fly-color-warning--light)',
  red: 'var(--fly-color-danger)',
  'red-light': 'var(--fly-color-danger--light)',
  background: 'var(--fly-color-bg)',
  'background-accent': 'var(--fly-color-bg--accent)',
  'background-alt': 'var(--fly-color-bg--alt)',
  body: 'var(--fly-color-body)',
  'body-light': 'var(--fly-color-body-light)',
  'body-accent': 'var(--fly-color-body-alt)',
  heading: 'var(--fly-color-headline)',
  'heading-accent': 'var(--fly-color-headline--caption)',
};

const variantTypeRegex = new RegExp(
  `(${VariantTypes.map(
    type => type.charAt(0).toUpperCase() + type.slice(1)
  ).join('|')})$`
);

const colorMappings = Object.entries(VariantColor).map(([key, variant]) => {
  const type: ColorMapping['type'] = (() => {
    const match = key.match(variantTypeRegex);
    if (!match) return DEFAULT_VARIANT_TYPE;
    return match[VARIANT_TYPE_GROUP].toLowerCase() as VariantType;
  })();

  const base =
    BaseColor[key.replace(variantTypeRegex, '') as keyof typeof BaseColor];

  return {base, variant, type} as ColorMapping;
});

const variantToBase = new Map(colorMappings.map(m => [m.variant, m.base]));
const baseToVariants = new Map(
  Object.values(BaseColor).map(color => [
    color,
    colorMappings.filter(m => m.base === color),
  ])
);

export const isVariantColor = (color: Color): color is VariantColor =>
  variantToBase.has(color as VariantColor);
export const isBaseColor = (color: Color): color is BaseColor =>
  baseToVariants.has(color as BaseColor);
export const getBaseColor = (variant: VariantColor): BaseColor | undefined =>
  variantToBase.get(variant);
export const getVariantColors = (base: BaseColor): ColorMapping[] =>
  baseToVariants.get(base) || [];

/**
 * Allows you to quickly generate color styles for use with the `style` attributes.
 */
export const getColorStyles = (props: ColorStyleProps): CSSProperties => {
  const styles: CSSProperties = {};

  if (props.color) styles.color = colorVarMap[props.color];
  if (props.bg) styles.backgroundColor = colorVarMap[props.bg];
  if (props.border) styles.borderColor = colorVarMap[props.border];

  return styles;
};

export function chooseReadableColor(name: string) {
  const colorsList = getReadableColors();
  return getValueByHash(colorsList, name);
}

export const UNREADABLE_COLORS: Color[] = [
  Color.Background,
  Color.BackgroundAccent,
  Color.BackgroundAlt,
  Color.Body,
  Color.BodyLight,
  Color.BodyAccent,
  Color.Heading,
  Color.HeadingAccent,
];

export function getReadableColors() {
  return Object.values(Color).filter(
    color => !UNREADABLE_COLORS.includes(color)
  );
}
